import type { Component, InjectionKey, Ref } from 'vue';
import { inject, provide, ref, onUnmounted } from 'vue';

/**
 * Teleport target ID for custom topbar content.
 * Use with `<Teleport :to="TOPBAR_CUSTOM_TARGET">` for fully custom actions.
 */
export const TOPBAR_CUSTOM_TARGET = '#topbar-custom-actions';

export interface TopbarAction {
  /** Unique identifier for the action */
  id: string;
  /** Icon component (from @remixicon/vue) - optional */
  icon?: Component;
  /** Button label - if omitted, renders as icon-only button */
  label?: string;
  /** Button type */
  type?: 'primary' | 'neutral' | 'error';
  /** Button variant */
  variant?: 'filled' | 'stroke' | 'lighter' | 'ghost';
  /** Click handler */
  onClick?: () => void;
  /** Sort order (lower = left) */
  order?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Aria label for accessibility (defaults to label if provided) */
  ariaLabel?: string;
}

export interface TopbarActionsContext {
  actions: Ref<TopbarAction[]>;
  registerAction: (action: TopbarAction) => void;
  unregisterAction: (id: string) => void;
}

const TOPBAR_ACTIONS_KEY: InjectionKey<TopbarActionsContext> = Symbol('topbar-actions');

/**
 * Provider for topbar actions - call this in the layout component
 */
export function provideTopbarActions(): TopbarActionsContext {
  const actions = ref<TopbarAction[]>([]);

  function registerAction(action: TopbarAction) {
    // Prevent duplicates
    const existingIndex = actions.value.findIndex((a) => a.id === action.id);
    if (existingIndex >= 0) {
      actions.value[existingIndex] = action;
    } else {
      actions.value.push(action);
    }
    // Sort by order
    actions.value.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  function unregisterAction(id: string) {
    actions.value = actions.value.filter((a) => a.id !== id);
  }

  const context: TopbarActionsContext = {
    actions,
    registerAction,
    unregisterAction,
  };

  provide(TOPBAR_ACTIONS_KEY, context);

  return context;
}

/**
 * Hook to register actions from child components.
 * 
 * @example Simple config-based action
 * ```ts
 * const { useAction } = useTopbarActions();
 * 
 * useAction({
 *   id: 'create',
 *   icon: RiAddLine,
 *   label: 'Create',
 *   type: 'primary',
 *   variant: 'filled',
 *   onClick: () => createItem(),
 * });
 * ```
 * 
 * @example For custom/complex actions, use Teleport
 * ```vue
 * <Teleport :to="TOPBAR_CUSTOM_TARGET">
 *   <MyCustomDropdown />
 * </Teleport>
 * ```
 */
export function useTopbarActions() {
  const context = inject(TOPBAR_ACTIONS_KEY);

  if (!context) {
    console.warn('[useTopbarActions] No TopbarActionsContext found. Make sure to use provideTopbarActions in a parent component.');
    return {
      actions: ref<TopbarAction[]>([]),
      registerAction: () => {},
      unregisterAction: () => {},
      useAction: () => {},
    };
  }

  /**
   * Register an action and automatically unregister on unmount
   */
  function useAction(action: TopbarAction) {
    context.registerAction(action);

    onUnmounted(() => {
      context.unregisterAction(action.id);
    });
  }

  return {
    ...context,
    useAction,
  };
}
